import { useIntl } from '@doxyme/locale';
import { Icon } from '@doxyme/icons';
import { Status } from '@doxyme/ui-foundation/components/status';
import { Tooltip } from '@doxyme/ui-foundation/components/tooltip';
import { useVideoIndicatorStyles } from './videoIndicator.styles';

import type { JSX } from 'react';

const videoIconSize = {
  small: 16,
  medium: 20,
  large: 24,
};

const statusSize = {
  small: 12,
  medium: 16,
  large: 20,
};

const spinnerSizeMap = {
  small: 20,
  medium: 24,
  large: 28,
};

export type VideoIndicatorProps = {
  hasVideo: boolean;
  hasProblem?: boolean;

  /**
   * When the surrounding self-preview is non-interactive (for example call on hold, matching
   * `VideoTile` `nonInteractive`): the control must not be clickable even if there is no
   * camera error. Permission or device failures use {@link hasProblem} instead. The button is
   * disabled when either flag is true.
   */
  isNonInteractive?: boolean;

  isLoading?: boolean;
  onClick?: () => void;
  size?: 'small' | 'medium' | 'large';
  tooltipContent?: string | { title: JSX.Element; ariaLabel: string };
};

/**
 * VideoIndicator displays an icon indicating the video status.
 * It can optionally show a warning status if there is a problem with the video.
 */
export const VideoIndicator = ({
  hasVideo,
  hasProblem = false,
  isNonInteractive = false,
  isLoading = false,
  size = 'large',
  tooltipContent,
  onClick = undefined,
}: VideoIndicatorProps): JSX.Element => {
  const { formatMessage } = useIntl();
  const { disabled, ...styles } = useVideoIndicatorStyles({
    hasVideo,
    hasProblem,
    isNonInteractive,
    isLoading,
    size,
    onClick,
  });

  const defaultTooltip = formatMessage({
    id: hasVideo
      ? 'videoIndicator.disableCamera'
      : 'videoIndicator.enableCamera',
  });
  const tooltip = tooltipContent ?? defaultTooltip;
  const withTooltip = hasProblem || (!disabled && !isLoading);

  const tooltipTitle = typeof tooltip === 'string' ? tooltip : tooltip.title;
  const ariaLabel = typeof tooltip === 'string' ? tooltip : tooltip.ariaLabel;

  const content = (
    <button
      aria-label={ariaLabel}
      css={styles.container}
      onClick={onClick}
      disabled={disabled}
      type='button'
      data-test-patient-click-tracking='video_tile_camera_toggle'
    >
      {isLoading ? (
        <Status statusLevel='spinner' size={spinnerSizeMap[size]} />
      ) : hasProblem ? (
        <>
          <Status
            size={statusSize[size]}
            statusLevel='error'
            css={styles.status}
          />
          <Icon.VideoDisabled size={videoIconSize[size]} css={styles.icon} />
        </>
      ) : disabled || !hasVideo ? (
        <Icon.VideoDisabled size={videoIconSize[size]} css={styles.icon} />
      ) : (
        <Icon.Video size={videoIconSize[size]} css={styles.icon} />
      )}
    </button>
  );

  if (withTooltip) {
    return (
      <Tooltip title={tooltipTitle} placement={hasProblem ? 'left' : undefined}>
        {disabled ? (
          /**
           * Wraps a disabled button with <span> to enable tooltips,
           * since disabled elements don’t trigger hover events.
           * @see https://mui.com/material-ui/react-tooltip/?srsltid=AfmBOorwpkAqWbH0bjRq1cS-Kqt6Htr1-eGoGCp7arQ01L9jq88eBSfz#disabled-elements
           */
          <span>{content}</span>
        ) : (
          content
        )}
      </Tooltip>
    );
  }

  return content;
};
